"use client";

import { Button } from "@/components/ui/button";
import { RefreshCw, Home, LoaderCircle } from "lucide-react";
import Link from "next/link";
import { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { reportClientError } from "@/lib/report-client-error";

export default function MarketingError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isRetrying, setIsRetrying] = useState(false);
  const retrying = isPending || isRetrying;

  useEffect(() => {
    if (
      error.name === "ChunkLoadError" ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("Failed to fetch dynamically imported module")
    ) {
      const key = "venturu_chunk_reload";
      try {
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          return;
        }
      } catch {
        // sessionStorage unavailable
      }
    }
    reportClientError(error);
  }, [error]);

  function handleRetry() {
    setIsRetrying(true);
    startTransition(() => {
      router.refresh();
      reset();
      setIsRetrying(false);
    });
  }

  return (
    <div className="flex min-h-[calc(100dvh-56px)] flex-col items-center justify-center px-6 py-16 md:min-h-[calc(100dvh-64px)]">
      <div className="text-center max-w-md">
        <div className="mb-6 font-mono text-[5rem] font-bold leading-none tracking-[-0.03em] text-foreground/[0.06] md:text-[7rem] select-none">
          500
        </div>

        <h1 className="text-section-h1 mb-3">Something went wrong</h1>
        <p className="text-body-secondary mb-8 text-pretty">
          We hit an unexpected error loading this page. This has been reported to
          our team automatically.
        </p>

        {error.digest && (
          <p className="text-xs text-muted-foreground mb-6">
            Reference: {error.digest}
          </p>
        )}

        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Button onClick={handleRetry} disabled={retrying} variant="default" size="lg" className="min-w-[180px]">
            {retrying ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {retrying ? "Retrying…" : "Try again"}
          </Button>
          <Button asChild variant="outline" size="lg" className="min-w-[180px]">
            <Link href="/">
              <Home className="mr-2 h-4 w-4" />
              Back to home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
